// src/modules/dl/FineTuningLab.jsx
// Fine-Tuning Lab — LoRA · PEFT · Full vs Partial · Catastrophic Forgetting · Adapter layers

import React, { useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--green)';
const P  = ['#4ade80','#22d3ee','#a78bfa','#fbbf24','#f87171','#fb923c'];

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);
const Sel = ({label,value,onChange,options}) => (
  <div style={{marginBottom:'8px'}}>
    {label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'3px'}}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'6px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

// ─── MATH ─────────────────────────────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };

const simFT = ({method, rank, lr, epochs, dataSize}) => {
  const multiplier = {full:1.4, lora:1.0, qlora:0.9, adapter:1.05, prefix:0.85, frozen:0.3}[method]||1;
  const forgetFactor = {full:0.15, lora:0.02, qlora:0.02, adapter:0.05, prefix:0.03, frozen:0}[method]||0;
  const pts=[]; let loss=2.0, baseLoss=1.2;
  for(let e=0;e<epochs;e++){
    const decay=0.04*lr*multiplier*(1+Math.log10(dataSize||100)/3);
    loss=Math.max(0.05,loss*(1-decay)+randn()*0.02);
    baseLoss=Math.max(0.8,baseLoss*(1+forgetFactor*0.01)+randn()*0.005);
    if(e%Math.max(1,Math.floor(epochs/80))===0)
      pts.push({epoch:e,loss:+loss.toFixed(4),basePerf:+baseLoss.toFixed(4)});
  }
  return pts;
};

const loraParams = (d, r) => 2*d*r;
const loraParamPct = (dModel, nLayers, r) => {
  const loraP = nLayers * (loraParams(dModel,r)*4); // 4 projections per layer
  const totalP = nLayers * (4*dModel*dModel); // QKV + output
  return (loraP/totalP*100).toFixed(2);
};

const TABS=['methods','lora','forgetting','peft-compare','data-scaling'];

// ─── LORA DIAGRAM SVG ────────────────────────────────────────────────────────
function LoraDiagram({dModel=256, rank=4}) {
  const W=600, H=220, pad=20;
  const boxW=100, boxH=60;
  const Wx=pad, Wy=(H-boxH)/2;
  const Ax=pad+boxW+60, Ay=pad;
  const Bx=pad+boxW+60, By=H-pad-boxH;
  const Oy=W-pad-boxW;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Frozen W */}
      <rect x={Wx} y={Wy} width={boxW} height={boxH} fill="#1e293b" stroke="#334155" rx={4}/>
      <text x={Wx+boxW/2} y={Wy+boxH/2} textAnchor="middle" dominantBaseline="middle" style={{fontSize:'10px',fill:'#94a3b8',fontFamily:'JetBrains Mono',fontWeight:700}}>W</text>
      <text x={Wx+boxW/2} y={Wy+boxH/2+14} textAnchor="middle" style={{fontSize:'8px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{dModel}×{dModel}</text>
      <text x={Wx+boxW/2} y={Wy-8} textAnchor="middle" style={{fontSize:'8px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>FROZEN</text>

      {/* A matrix */}
      <rect x={Ax} y={Ay} width={boxW} height={boxH} fill="#22d3ee18" stroke="#22d3ee" strokeWidth={1.5} rx={4}/>
      <text x={Ax+boxW/2} y={Ay+boxH/2} textAnchor="middle" dominantBaseline="middle" style={{fontSize:'10px',fill:'#22d3ee',fontFamily:'JetBrains Mono',fontWeight:700}}>A</text>
      <text x={Ax+boxW/2} y={Ay+boxH/2+14} textAnchor="middle" style={{fontSize:'8px',fill:'#22d3ee',fontFamily:'JetBrains Mono'}}>{rank}×{dModel}</text>
      <text x={Ax+boxW/2} y={Ay-8} textAnchor="middle" style={{fontSize:'8px',fill:'#22d3ee',fontFamily:'JetBrains Mono'}}>TRAINABLE</text>

      {/* B matrix */}
      <rect x={Bx} y={By} width={boxW} height={boxH} fill="#4ade8018" stroke="#4ade80" strokeWidth={1.5} rx={4}/>
      <text x={Bx+boxW/2} y={By+boxH/2} textAnchor="middle" dominantBaseline="middle" style={{fontSize:'10px',fill:'#4ade80',fontFamily:'JetBrains Mono',fontWeight:700}}>B</text>
      <text x={Bx+boxW/2} y={By+boxH/2+14} textAnchor="middle" style={{fontSize:'8px',fill:'#4ade80',fontFamily:'JetBrains Mono'}}>{dModel}×{rank}</text>
      <text x={Bx+boxW/2} y={By-8} textAnchor="middle" style={{fontSize:'8px',fill:'#4ade80',fontFamily:'JetBrains Mono'}}>TRAINABLE</text>

      {/* Output merge */}
      <rect x={W-pad-boxW} y={(H-boxH)/2} width={boxW} height={boxH} fill="#fbbf2418" stroke="#fbbf24" rx={4}/>
      <text x={W-pad-boxW+boxW/2} y={(H-boxH)/2+boxH/2-6} textAnchor="middle" dominantBaseline="middle" style={{fontSize:'10px',fill:'#fbbf24',fontFamily:'JetBrains Mono',fontWeight:700}}>W + ΔW</text>
      <text x={W-pad-boxW+boxW/2} y={(H-boxH)/2+boxH/2+8} textAnchor="middle" style={{fontSize:'8px',fill:'#fbbf24',fontFamily:'JetBrains Mono'}}>ΔW = B·A·α/r</text>

      {/* Arrows */}
      {/* W → output */}
      <line x1={Wx+boxW} y1={H/2} x2={W-pad-boxW} y2={(H)/2} stroke="#33415588" strokeWidth={2} strokeDasharray="4 2"/>
      {/* input → A */}
      <line x1={Wx+boxW+10} y1={H/2} x2={Ax} y2={Ay+boxH/2} stroke="#22d3ee66" strokeWidth={1.5}/>
      {/* A → B */}
      <line x1={Ax+boxW/2} y1={Ay+boxH} x2={Bx+boxW/2} y2={By} stroke="#fbbf2466" strokeWidth={1.5}/>
      {/* B → output */}
      <line x1={Bx+boxW} y1={By+boxH/2} x2={W-pad-boxW} y2={(H)/2} stroke="#4ade8066" strokeWidth={1.5}/>

      {/* Rank annotation */}
      <line x1={Ax+boxW+5} y1={Ay+boxH/2} x2={Bx+boxW+5} y2={By+boxH/2} stroke="#fbbf2444" strokeWidth={1} strokeDasharray="3 2"/>
      <text x={Ax+boxW+18} y={(Ay+boxH/2+By+boxH/2)/2} textAnchor="middle" style={{fontSize:'9px',fill:'#fbbf24',fontFamily:'JetBrains Mono'}}>rank={rank}</text>
    </svg>
  );
}

export default function FineTuningLab() {
  const [tab, setTab] = useState('methods');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Fine-Tuning Lab — compare methods, visualize LoRA, observe catastrophic forgetting.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [method, setMethod] = useState('lora');
  const [rank, setRank] = useState(8);
  const [alpha, setAlpha] = useState(16);
  const [lr, setLr] = useState(0.0003);
  const [epochs, setEpochs] = useState(100);
  const [dataSize, setDataSize] = useState(1000);
  const [dModel, setDModel] = useState(768);
  const [nLayers, setNLayers] = useState(12);

  const [ftResult, setFtResult] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [forgetResult, setForgetResult] = useState(null);
  const [dataScaleResult, setDataScaleResult] = useState(null);

  const trainableParams = {
    full:   dModel*dModel*4*nLayers,
    lora:   nLayers*loraParams(dModel,rank)*4,
    qlora:  Math.floor(nLayers*loraParams(dModel,rank)*4*0.5),
    adapter: nLayers*(dModel*64*2),
    prefix:  nLayers*50*dModel*2,
    frozen:  0,
  };

  const runFT = () => {
    const data = simFT({method, rank, lr, epochs, dataSize});
    setFtResult(data);
    log(`${method.toUpperCase()}: r=${rank} α=${alpha} lr=${lr} epochs=${epochs} data=${dataSize} | final loss=${data.at(-1)?.loss}`,'ok');
  };

  const runCompare = () => {
    const methods=['full','lora','qlora','adapter','prefix','frozen'];
    const results=methods.map(m=>{
      const data=simFT({method:m,rank,lr,epochs,dataSize});
      return {method:m, data, finalLoss:data.at(-1)?.loss, basePerf:data.at(-1)?.basePerf, params:trainableParams[m]||0};
    });
    const chart=results[0].data.map((p,i)=>{const pt={epoch:p.epoch};results.forEach(r=>{pt[r.method]=r.data[i]?.loss;});return pt;});
    setCompareResult({chart,finals:results.sort((a,b)=>a.finalLoss-b.finalLoss)});
    log(`Compare: ${methods.join(', ')} | best: ${results.sort((a,b)=>a.finalLoss-b.finalLoss)[0].method}`,'ok');
  };

  const runForgetting = () => {
    const taskAData=Array.from({length:epochs},(_,e)=>({epoch:e,loss:+(2*Math.exp(-e*0.04)+0.05+randn()*0.01).toFixed(4)}));
    // Catastrophic forgetting after task B starts
    const fullFT=taskAData.map((p,i)=>({...p,taskA:+(p.loss*(1+i*0.005)+randn()*0.01).toFixed(4)}));
    const loraFT=taskAData.map((p,i)=>({...p,taskA:+(p.loss*(1+i*0.0003)+randn()*0.005).toFixed(4)}));
    const ewcFT=taskAData.map((p,i)=>({...p,taskA:+(p.loss*(1+i*0.001)+randn()*0.005).toFixed(4)}));
    const chart=Array.from({length:epochs},(_,i)=>({epoch:i,full:fullFT[i].taskA,lora:loraFT[i].taskA,ewc:ewcFT[i].taskA}));
    setForgetResult(chart);
    log(`Catastrophic forgetting: Full FT causes task-A degradation; LoRA/EWC protect it`,'ok');
  };

  const runDataScaling = () => {
    const sizes=[10,50,100,500,1000,5000,10000,50000];
    const result=sizes.map(n=>{
      const d=simFT({method,rank,lr,epochs:80,dataSize:n});
      return {n:`${n>=1000?n/1000+'K':n}`,samples:n,finalLoss:d.at(-1)?.loss,lora_r4:+(d.at(-1)?.loss+0.05+3/Math.sqrt(n)).toFixed(4),full:+(d.at(-1)?.loss-0.1+10/Math.sqrt(n)).toFixed(4)};
    });
    setDataScaleResult(result);
    log(`Data scaling: ${sizes.join(', ')} samples | LoRA efficient at small data`,'ok');
  };

  const loraParamPctVal = loraParamPct(dModel, nLayers, rank);

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* Config strip */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr auto',gap:'10px',padding:'12px',background:'var(--surface)',border:'1px solid var(--b0)',borderRadius:'4px',alignItems:'end'}}>
        <Sel label="METHOD" value={method} onChange={setMethod} options={[{value:'full',label:'Full Fine-Tune'},{value:'lora',label:'LoRA'},{value:'qlora',label:'QLoRA (4-bit)'},{value:'adapter',label:'Adapter Layers'},{value:'prefix',label:'Prefix Tuning'},{value:'frozen',label:'Frozen (eval only)'}]}/>
        <Sl label="LoRA Rank r" value={rank} min={1} max={64} step={1} onChange={setRank} color="#22d3ee"/>
        <Sl label="Learning Rate" value={lr} min={0.00001} max={0.01} step={0.00001} onChange={setLr} color="#4ade80"/>
        <Sl label="Dataset Size" value={dataSize} min={10} max={50000} step={10} onChange={setDataSize} color="#fbbf24"/>
        <Sl label="Epochs" value={epochs} min={20} max={300} step={10} onChange={setEpochs} color="#a78bfa"/>
        <Btn onClick={runFT} color={AC}>TRAIN →</Btn>
      </div>

      {/* ── METHODS ── */}
      {tab==='methods'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          <Panel title="TRAINABLE PARAMETERS BY METHOD" accent={AC}>
            <div style={{display:'flex',flexDirection:'column',gap:'5px'}}>
              {Object.entries(trainableParams).map(([m,p],i)=>{
                const maxP=trainableParams.full||1;
                const pct=(p/maxP*100);
                return (
                  <div key={m} style={{display:'flex',alignItems:'center',gap:'8px'}}>
                    <span style={{fontSize:'10px',color:P[i%P.length],fontFamily:'JetBrains Mono',fontWeight:700,minWidth:72}}>{m}</span>
                    <div style={{flex:1,height:16,background:'var(--b0)',borderRadius:2,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${pct}%`,background:`linear-gradient(90deg,${P[i%P.length]}44,${P[i%P.length]})`,borderRadius:2,display:'flex',alignItems:'center',paddingLeft:4,fontSize:'8px',color:'#000',fontWeight:700}}>
                        {pct>3?`${pct.toFixed(1)}%`:''}
                      </div>
                    </div>
                    <span style={{fontSize:'9px',color:'var(--t3)',fontFamily:'JetBrains Mono',minWidth:70,textAlign:'right'}}>{p>=1e6?`${(p/1e6).toFixed(1)}M`:p>=1e3?`${(p/1e3).toFixed(0)}K`:p}</span>
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel title="METHOD COMPARISON" accent="#22d3ee">
            {[
              {name:'Full FT',params:'100%',memory:'High',forgetting:'High',data:'Need lots',color:'#f87171'},
              {name:'LoRA',params:`~${loraParamPctVal}%`,memory:'Low',forgetting:'Low',data:'100-1K samples',color:'#4ade80'},
              {name:'QLoRA',params:`~${loraParamPctVal}%`,memory:'Very Low',forgetting:'Low',data:'100-1K samples',color:'#22d3ee'},
              {name:'Adapter',params:'~5%',memory:'Low',forgetting:'Medium',data:'1K+ samples',color:'#a78bfa'},
              {name:'Prefix',params:'~0.1%',memory:'Low',forgetting:'Low',data:'100+ samples',color:'#fbbf24'},
              {name:'Frozen',params:'0%',memory:'Zero',forgetting:'Zero',data:'0 (eval only)',color:'#64748b'},
            ].map(m=>(
              <div key={m.name} style={{display:'grid',gridTemplateColumns:'80px 1fr 1fr 1fr',gap:'6px',padding:'6px 8px',background:'var(--deep)',borderRadius:'3px',marginBottom:'4px',borderLeft:`2px solid ${m.color}`}}>
                <span style={{fontSize:'10px',color:m.color,fontWeight:700,fontFamily:'JetBrains Mono'}}>{m.name}</span>
                <span style={{fontSize:'9px',color:'var(--t2)'}}>{m.params}</span>
                <span style={{fontSize:'9px',color:m.memory==='High'?'#f87171':m.memory==='Very Low'?'#4ade80':'#fbbf24'}}>{m.memory}</span>
                <span style={{fontSize:'9px',color:m.forgetting==='High'?'#f87171':m.forgetting==='Low'?'#4ade80':'#fbbf24'}}>{m.forgetting}</span>
              </div>
            ))}
          </Panel>

          {ftResult&&(
            <Panel title="TRAINING CURVES" accent={AC} style={{gridColumn:'span 2'}}>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={ftResult}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <Tooltip/>
                  <Legend/>
                  <Line type="monotone" dataKey="loss" stroke="#4ade80" strokeWidth={2} dot={false} name="Fine-tune Loss"/>
                  <Line type="monotone" dataKey="basePerf" stroke="#f87171" strokeWidth={2} strokeDasharray="5 3" dot={false} name="Base Task Loss"/>
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          )}
        </div>
      )}

      {/* ── LORA ── */}
      {tab==='lora'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title={`LORA ARCHITECTURE — d_model=${dModel} rank=${rank} α=${alpha}`} accent="#22d3ee">
            <div style={{marginBottom:'8px',fontSize:'10px',color:'var(--t3)'}}>
              W is frozen. ΔW = B·A is learned. Effective weight = W + ΔW·(α/r). Only A and B are trained.
            </div>
            <LoraDiagram dModel={dModel} rank={rank}/>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'10px'}}>
            <Metric label="Trainable Params" value={`${(nLayers*loraParams(dModel,rank)*4/1e6).toFixed(2)}M`} color="#22d3ee"/>
            <Metric label="% of Full Model" value={`${loraParamPctVal}%`} color="#4ade80"/>
            <Metric label="Memory vs Full" value={`${(100-parseFloat(loraParamPctVal)).toFixed(1)}% saved`} color="#a78bfa"/>
            <Metric label="Effective ΔW scale" value={`α/r = ${(alpha/rank).toFixed(2)}`} color="#fbbf24"/>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="RANK SENSITIVITY — HOW RANK r AFFECTS PARAMS vs QUALITY" accent="#fbbf24">
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={[1,2,4,8,16,32,64].map(r=>({r,params:+(nLayers*loraParams(dModel,r)*4/1e6).toFixed(2),pct:+loraParamPct(dModel,nLayers,r)}))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="r" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'rank',fill:'var(--t3)',fontSize:9,position:'insideBottom',offset:-2}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <Tooltip/>
                  <Legend/>
                  <ReferenceLine x={rank} stroke="#fbbf24" strokeDasharray="5 3" label={{value:'current',fill:'#fbbf24',fontSize:8}}/>
                  <Line type="monotone" dataKey="params" stroke="#22d3ee" strokeWidth={2} dot={{r:3}} name="Params (M)"/>
                  <Line type="monotone" dataKey="pct" stroke="#4ade80" strokeWidth={2} dot={{r:3}} name="% of full model"/>
                </LineChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="LoRA HYPERPARAMETER GUIDE" accent="#a78bfa">
              {[
                {param:'rank r',vals:'4, 8, 16, 32',tip:'Higher = more capacity. r=8 is good default.'},
                {param:'alpha α',vals:'r, 2r, 32',tip:'Scale for ΔW. α=r → scale=1. α=2r often works.'},
                {param:'LR',vals:'1e-4 to 3e-3',tip:'Much higher than full FT. AdamW preferred.'},
                {param:'target',vals:'q_proj, v_proj',tip:'Usually attention projections. Add more for harder tasks.'},
                {param:'dropout',vals:'0, 0.05, 0.1',tip:'Regularize if overfitting on small data.'},
              ].map(h=>(
                <div key={h.param} style={{marginBottom:'7px',padding:'7px',background:'var(--deep)',borderRadius:'3px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:'2px'}}>
                    <span style={{fontSize:'10px',color:'#a78bfa',fontFamily:'JetBrains Mono',fontWeight:700}}>{h.param}</span>
                    <span style={{fontSize:'9px',color:'#fbbf24',fontFamily:'JetBrains Mono'}}>{h.vals}</span>
                  </div>
                  <div style={{fontSize:'9px',color:'var(--t3)'}}>{h.tip}</div>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      )}

      {/* ── FORGETTING ── */}
      {tab==='forgetting'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Btn onClick={runForgetting} color="#f87171" style={{alignSelf:'flex-start'}}>SIMULATE FORGETTING →</Btn>
          {forgetResult
            ? <>
                <Panel title="CATASTROPHIC FORGETTING — TASK-A PERFORMANCE DURING TASK-B FINE-TUNING" accent="#f87171">
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>Full FT destroys original knowledge. LoRA and EWC protect it by constraining which parameters change.</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={forgetResult}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/>
                      <Legend/>
                      <Line type="monotone" dataKey="full" stroke="#f87171" strokeWidth={2} dot={false} name="Full FT (forgetting ⬆)"/>
                      <Line type="monotone" dataKey="lora" stroke="#4ade80" strokeWidth={2} dot={false} name="LoRA (stable)"/>
                      <Line type="monotone" dataKey="ewc" stroke="#22d3ee" strokeWidth={2} dot={false} strokeDasharray="5 3" name="EWC (elastic weight)"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
                  {[
                    {name:'Full Fine-Tune',color:'#f87171',forget:'HIGH — overwrites weights',mechanism:'All params updated freely',solution:'Use LoRA or EWC'},
                    {name:'LoRA',color:'#4ade80',forget:'LOW — frozen base model',mechanism:'Only low-rank deltas updated',solution:'Best for most use cases'},
                    {name:'EWC',color:'#22d3ee',forget:'LOW — penalizes important weights',mechanism:'Fisher info constrains updates',solution:'Useful for continual learning'},
                  ].map(m=>(
                    <Panel key={m.name} title={m.name} accent={m.color}>
                      {[['Forgetting',m.forget,m.color],['How',m.mechanism,'var(--t2)'],['When',m.solution,'#fbbf24']].map(([k,v,c])=>(
                        <div key={k} style={{marginBottom:'5px',fontSize:'9px',lineHeight:1.5}}>
                          <span style={{color:'var(--t3)',marginRight:5}}>{k}:</span><span style={{color:c}}>{v}</span>
                        </div>
                      ))}
                    </Panel>
                  ))}
                </div>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'200px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Simulate forgetting</div>
          }
          <LogPanel logs={logs} height={70}/>
        </div>
      )}

      {/* ── PEFT COMPARE ── */}
      {tab==='peft-compare'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Btn onClick={runCompare} color={AC} style={{alignSelf:'flex-start'}}>RUN ALL METHODS →</Btn>
          {compareResult
            ? <>
                <Panel title="FINE-TUNE LOSS — ALL METHODS" accent={AC}>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={compareResult.chart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/>
                      <Legend/>
                      {compareResult.finals.map((r,i)=><Line key={r.method} type="monotone" dataKey={r.method} stroke={P[i%P.length]} strokeWidth={2} dot={false}/>)}
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
                <Panel title="FINAL LEADERBOARD" accent={AC}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                    <thead><tr style={{background:'var(--deep)'}}>
                      {['Rank','Method','Final Loss','Base Perf','Params'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {compareResult.finals.map((r,i)=>(
                        <tr key={r.method} style={{borderBottom:'1px solid var(--b0)'}}>
                          <td style={{padding:'7px 10px',color:P[i%P.length],fontFamily:'Syne',fontWeight:800,fontSize:'16px'}}>{i+1}</td>
                          <td style={{padding:'7px 10px',color:P[i%P.length],fontWeight:700}}>{r.method}</td>
                          <td style={{padding:'7px 10px',color:'#4ade80',fontFamily:'JetBrains Mono'}}>{r.finalLoss}</td>
                          <td style={{padding:'7px 10px',color:r.basePerf>1.5?'#f87171':'#4ade80',fontFamily:'JetBrains Mono'}}>{r.basePerf}</td>
                          <td style={{padding:'7px 10px',color:'#22d3ee',fontFamily:'JetBrains Mono'}}>{r.params>=1e6?`${(r.params/1e6).toFixed(1)}M`:r.params>=1000?`${(r.params/1000).toFixed(0)}K`:r.params}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Panel>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Run all PEFT methods</div>
          }
        </div>
      )}

      {/* ── DATA SCALING ── */}
      {tab==='data-scaling'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Btn onClick={runDataScaling} color="#fbbf24" style={{alignSelf:'flex-start'}}>RUN DATA SCALING →</Btn>
          {dataScaleResult
            ? <>
                <Panel title="FINE-TUNE LOSS vs DATASET SIZE — LoRA OUTPERFORMS FULL FT AT SMALL DATA" accent="#fbbf24">
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={dataScaleResult}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="n" tick={{fontSize:10,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/>
                      <Legend/>
                      <Line type="monotone" dataKey="lora_r4" stroke="#4ade80" strokeWidth={2} dot={{r:4}} name="LoRA r=4"/>
                      <Line type="monotone" dataKey="full" stroke="#f87171" strokeWidth={2} dot={{r:4}} name="Full FT"/>
                      <Line type="monotone" dataKey="finalLoss" stroke="#22d3ee" strokeWidth={2} dot={{r:4}} name={`${method} (current)`}/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                  {[
                    ['Best dataset size','100-1K samples for LoRA','#4ade80'],
                    ['Full FT needs','10K+ samples','#f87171'],
                    ['QLoRA @ 4-bit','Same quality, 2× less VRAM','#22d3ee'],
                    ['Rule of thumb','10-20 samples/concept for LoRA','#fbbf24'],
                  ].map(([k,v,c])=>(
                    <Panel key={k} title={k} accent={c}>
                      <div style={{fontSize:'10px',color:c,fontWeight:700,fontFamily:'JetBrains Mono'}}>{v}</div>
                    </Panel>
                  ))}
                </div>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Run data scaling analysis</div>
          }
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
